import { createContext, useContext, useState } from 'react';
import { OFFER_VARIANTS, OFFER_STORAGE_KEY } from './offers.js';

/* Offer-test variant assignment (Design handoff 2026-06-11).
 *
 * Visitors are routed into a variant via the landing-page URL:
 *   https://…/?offer=v1   (any path works, e.g. /learn/welsh?offer=v3)
 * The assignment persists in sessionStorage for the whole visit, so every
 * funnel page (and any detour via /about, /community, …) keeps rendering
 * the same offer — containment is state, not link rewriting. No ?offer=
 * param and no stored value → control (the current 7-day-trial funnel,
 * byte-identical to pre-test rendering).
 *
 * ?offer=off clears a stored assignment (handy for testing).
 *
 * Conversion tracking (Ivan): the active variant is exposed as
 *   window.SSI_VARIANT            – 'v1'…'v4', unset on control
 *   dataLayer 'ssi_offer_variant' – pushed once per page load for GTM
 * and /pay forwards it to the backend as &v= on the onestep call so it
 * can be stamped into Stripe metadata alongside the attribution source.
 */

const OfferContext = createContext(undefined);

function resolveVariant() {
  let fromUrl = null;
  try {
    fromUrl = new URLSearchParams(window.location.search).get('offer');
  } catch (e) { /* ignore */ }

  if (fromUrl === 'off') {
    try { sessionStorage.removeItem(OFFER_STORAGE_KEY); } catch (e) { /* ignore */ }
    return null;
  }

  if (fromUrl && OFFER_VARIANTS[fromUrl]) {
    try { sessionStorage.setItem(OFFER_STORAGE_KEY, fromUrl); } catch (e) { /* ignore */ }
    return fromUrl;
  }

  try {
    const stored = sessionStorage.getItem(OFFER_STORAGE_KEY);
    if (stored && OFFER_VARIANTS[stored]) return stored;
  } catch (e) { /* ignore */ }

  return null;
}

export function OfferProvider({ children }) {
  const [value] = useState(() => {
    const variant = resolveVariant();
    return variant ? { variant, config: OFFER_VARIANTS[variant] } : null;
  });

  if (typeof window !== 'undefined') {
    window.SSI_VARIANT = value ? value.variant : undefined;
    if (value && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'ssi_offer_variant', ssiVariant: value.variant });
    }
  }

  return (
    <OfferContext.Provider value={value}>
      {children}
    </OfferContext.Provider>
  );
}

// Returns null on control, { variant, config } when an offer is active.
export function useOffer() {
  const ctx = useContext(OfferContext);
  if (ctx === undefined) {
    throw new Error('useOffer must be used inside <OfferProvider>');
  }
  return ctx;
}

// Merge a page's tweak defaults with the active variant's overrides.
// `pages` is one or more keys into config.tweaks ('home', 'pricing', …);
// 'shared' is always applied first.
export function useOfferTweakDefaults(defaults, ...pages) {
  const offer = useOffer();
  if (!offer) return defaults;
  const merged = { ...defaults, ...(offer.config.tweaks.shared || {}) };
  for (const page of pages) {
    Object.assign(merged, offer.config.tweaks[page] || {});
  }
  return merged;
}
